import { type QueueService } from './QueueService'
import { type QueueEntryType } from '../../model/queue/QueueEntry'
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  runTransaction
} from 'firebase/firestore'
import { auth, db } from '../../db'
import {
  dateKeyInTimeZone,
  normalizeAppointmentSettings
} from '../../utils/appointmentUtils'
import { type AppointmentSettings } from '../../model/appointment'
import { getHighestQueuePosition } from './queuePosition'

async function getClinicDateKey(doctorId: string): Promise<string> {
  const doctorSnapshot = await getDoc(doc(db, 'doctor', doctorId))
  const settings = normalizeAppointmentSettings(
    doctorSnapshot.data()?.appointmentSettings as
      | Partial<AppointmentSettings>
      | undefined
  )
  return dateKeyInTimeZone(Date.now(), settings.timezone)
}

export class QueueServiceImpl implements QueueService {
  private static readonly collectionName = 'queue'

  getTodayQueue(
    doctorId: string,
    onUpdate: (entries: QueueEntryType[]) => void,
    onError?: (error: Error) => void
  ): () => void {
    let unsubscribe = () => {}
    let cancelled = false

    void getClinicDateKey(doctorId)
      .then((today) => {
        if (cancelled) return
        const queueQuery = query(
          collection(db, QueueServiceImpl.collectionName),
          where('doctorId', '==', doctorId),
          where('date', '==', today),
          orderBy('position')
        )
        unsubscribe = onSnapshot(
          queueQuery,
          (snapshot) => {
            onUpdate(
              snapshot.docs.map((snapshotDoc) => ({
                id: snapshotDoc.id,
                ...(snapshotDoc.data() as Omit<QueueEntryType, 'id'>)
              }))
            )
          },
          (error) => {
            if (onError) onError(error)
            else onUpdate([])
          }
        )
      })
      .catch((error: Error) => {
        if (!cancelled) {
          if (onError) onError(error)
          else onUpdate([])
        }
      })

    return () => {
      cancelled = true
      unsubscribe()
    }
  }

  async addToQueue(
    entry: Omit<
      QueueEntryType,
      'id' | 'position' | 'addedAt' | 'date' | 'status'
    > & { doctorId: string; patientId: string; patientName: string }
  ): Promise<QueueEntryType> {
    const date = await getClinicDateKey(entry.doctorId)
    // Existing clinics may have queue rows created before counters existed.
    // Seed a missing/stale counter from the highest stored position.
    const highestStoredPosition = await getHighestQueuePosition(
      entry.doctorId,
      date
    )
    return await runTransaction(db, async (transaction) => {
      const counterRef = doc(db, 'queueCounters', `${entry.doctorId}_${date}`)
      const counterSnapshot = await transaction.get(counterRef)
      const position =
        Math.max(
          highestStoredPosition,
          Number(counterSnapshot.data()?.nextPosition ?? 0)
        ) + 1
      const queueRef = doc(collection(db, QueueServiceImpl.collectionName))
      const newEntry: Omit<QueueEntryType, 'id'> = {
        doctorId: entry.doctorId,
        patientId: entry.patientId,
        patientName: entry.patientName,
        date,
        addedAt: Date.now(),
        status: 'waiting',
        position,
        source: entry.source ?? 'walk-in',
        ...(entry.appointmentId ? { appointmentId: entry.appointmentId } : {}),
        ...(entry.scheduledAt ? { scheduledAt: entry.scheduledAt } : {})
      }
      transaction.set(queueRef, newEntry)
      transaction.set(
        counterRef,
        {
          doctorId: entry.doctorId,
          date,
          nextPosition: position,
          updatedAt: Date.now()
        },
        { merge: true }
      )
      return { id: queueRef.id, ...newEntry }
    })
  }

  async updateStatus(
    id: string,
    status: QueueEntryType['status']
  ): Promise<void> {
    await runTransaction(db, async (transaction) => {
      const queueRef = doc(db, QueueServiceImpl.collectionName, id)
      const queueSnapshot = await transaction.get(queueRef)
      if (!queueSnapshot.exists()) throw new Error('Queue entry not found')
      const entry = queueSnapshot.data() as Omit<QueueEntryType, 'id'>
      transaction.update(queueRef, { status })
      if (status === 'done' && entry.appointmentId) {
        transaction.update(doc(db, 'appointments', entry.appointmentId), {
          status: 'completed',
          updatedBy: auth.currentUser?.uid ?? entry.doctorId,
          updatedAt: Date.now()
        })
      }
    })
  }

  async updatePosition(
    entryA: QueueEntryType,
    entryB: QueueEntryType
  ): Promise<void> {
    await runTransaction(db, async (transaction) => {
      const refA = doc(db, QueueServiceImpl.collectionName, entryA.id)
      const refB = doc(db, QueueServiceImpl.collectionName, entryB.id)
      const [snapshotA, snapshotB] = await Promise.all([
        transaction.get(refA),
        transaction.get(refB)
      ])
      if (!snapshotA.exists() || !snapshotB.exists()) {
        throw new Error('Queue entry not found')
      }
      const currentA = snapshotA.data() as Omit<QueueEntryType, 'id'>
      const currentB = snapshotB.data() as Omit<QueueEntryType, 'id'>
      if (
        currentA.doctorId !== currentB.doctorId ||
        currentA.date !== currentB.date ||
        currentA.status !== 'waiting' ||
        currentB.status !== 'waiting'
      ) {
        throw new Error('Queue entries cannot be reordered')
      }
      transaction.update(refA, { position: currentB.position })
      transaction.update(refB, { position: currentA.position })
    })
  }

  async removeFromQueue(id: string, doctorId: string): Promise<void> {
    await runTransaction(db, async (transaction) => {
      const queueRef = doc(db, QueueServiceImpl.collectionName, id)
      const queueSnapshot = await transaction.get(queueRef)
      if (!queueSnapshot.exists()) return
      const entry = queueSnapshot.data() as Omit<QueueEntryType, 'id'>
      if (entry.doctorId !== doctorId) throw new Error('Queue owner mismatch')
      if (entry.status === 'waiting' && entry.appointmentId) {
        transaction.update(doc(db, 'appointments', entry.appointmentId), {
          status: 'scheduled',
          updatedBy: auth.currentUser?.uid ?? doctorId,
          updatedAt: Date.now()
        })
      }
      transaction.delete(queueRef)
    })
  }
}
